
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { teamPhotoService } from '@/services/teamPhotoService';
import { Camera, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

interface TeamLogoUploadProps {
  onLogoUpload: (logoPath: string) => void;
  currentLogo?: string;
  teamName?: string;
}

export const TeamLogoUpload: React.FC<TeamLogoUploadProps> = ({
  onLogoUpload,
  currentLogo,
  teamName
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentLogo || null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
      toast.error('Please upload a JPEG or PNG image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Create a descriptive filename for the team logo
      const sanitizedTeamName = teamName ? teamName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase() : 'team';
      const fileName = `team-logo-${sanitizedTeamName}-${Date.now()}`;

      // Upload using teamPhotoService
      const result = await teamPhotoService.uploadTeamLogo(file, fileName);

      if (!result.success) {
        toast.error(result.error || 'Failed to upload team logo');
        return;
      }

      if (result.url) {
        onLogoUpload(result.url);
        toast.success('Team logo uploaded successfully!');
      }

    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload team logo');
    } finally {
      setUploading(false);
    }
  };

  const clearLogo = () => {
    setPreview(null);
    onLogoUpload('');
  };

  return (
    <div className="space-y-4">
      <Label className="text-gray-300">Team Logo</Label>
      
      <div className="flex items-center gap-4">
        {/* Logo Preview */}
        <div className="w-20 h-20 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden border-2 border-gray-600">
          {preview ? (
            <img 
              src={preview} 
              alt="Team logo preview" 
              className="w-full h-full object-cover"
            />
          ) : (
            <Camera className="h-8 w-8 text-gray-400" />
          )}
        </div>

        {/* Upload Controls */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="team-logo-upload" className="cursor-pointer">
            <div className="bg-sports-green text-black px-4 py-2 rounded-lg hover:bg-green-600 transition-colors inline-flex items-center gap-2 text-sm font-medium">
              <Upload className="h-4 w-4" />
              {uploading ? 'Uploading...' : 'Upload Logo'}
            </div>
          </Label>
          
          <Input
            id="team-logo-upload"
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            onChange={handleFileChange}
            disabled={uploading}
            className="hidden"
          />

          {preview && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearLogo}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <X className="h-3 w-3 mr-1" />
              Remove
            </Button>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-400">
        Upload JPEG or PNG format. Max size 5MB.
      </p>
    </div>
  );
};
